"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { applyPendingProfile } from "@/lib/auth/role";
import type { User, Session } from "@supabase/supabase-js";

/**
 * Whether the path the user was on when they signed out must be left behind.
 * Anything auth-gated (or that surfaces user data) gets bounced to "/" so the
 * next user on this browser doesn't see a flash of the previous user's UI.
 */
function isPrivatePath(pathname: string): boolean {
  return (
    pathname.startsWith("/app") ||
    pathname.startsWith("/account") ||
    pathname.startsWith("/checkout")
  );
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  configured: boolean;
  signOut: () => Promise<void>;
}

// Exported so the dev-only nav harness (/dev-nav-preview) can provide a
// mocked signed-in user without a Supabase session. App code should consume
// auth exclusively through useAuth().
export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  configured: false,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const supabase = createClient();
  const configured = supabase !== null;

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session: s } }: { data: { session: Session | null } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      // Capture the role/marketing an OAuth signup stashed before redirecting.
      // Self-guards (clears its stash on read) so this no-ops for everyone else.
      if (s?.user) void applyPendingProfile(supabase, s.user);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: string, s: Session | null) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      if (s?.user) void applyPendingProfile(supabase, s.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    const client = createClient();
    if (client) {
      const { error } = await client.auth.signOut();
      if (error) {
        // Surface the failure rather than swallowing it. The local user/
        // session state still gets cleared below as a defensive fallback,
        // so the UI reflects "signed out" even when the network call failed.
        console.error("[AuthContext] signOut failed:", error.message);
      }
    }
    // Clear local React state immediately — the onAuthStateChange listener
    // would do this too, but only after Supabase round-trips. Clearing here
    // prevents a flash of the previous user's data while we navigate away.
    setUser(null);
    setSession(null);
    // Hard-redirect away from any private surface so the next user on this
    // browser starts at the public landing, not at /app or /account/*.
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (isPrivatePath(path)) {
        // Use location.assign (full reload) rather than router.push so React
        // state from the signed-in session doesn't linger across the SPA
        // boundary. Belt-and-suspenders for cached hooks.
        window.location.assign("/");
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, configured, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
